<script setup lang="ts">
definePageMeta({ layout: 'documentation' })
useSeoMeta({
  title: 'Finish Line Video Processing - Documentation'
})

const faq = [
  {
    question: 'What if the AI misses a boat?',
    answer: 'Just click Add Finish and manually input the sail number and finish time.'
  },
  {
    question: 'Can I re-process a video?',
    answer: 'Not from the same upload — there\'s no way to delete its results and reprocess from scratch. If something\'s wrong, correct it directly in the results table instead: edit any field, add a finish the AI missed, or remove an individual detected finish row.'
  },
  {
    question: 'What does "Finish Code" mean?',
    answer: 'Use this for special race outcomes such as did not finish (DNF), did not start (DNS) or disqualified (DSQ).'
  }
]
</script>
<template>
  <div>
    <h1>Finish Line Video Processing</h1>
    <v-row>
      <v-col cols="12" md="6">
        <p>
          The Finish Line Video Processing feature enables users to upload race finish line videos from sailing
          competitions. The system uses AI to automatically extract:
        </p>
        <ul>
          <li>Race start time</li>
          <li>Finish times</li>
          <li>Sail numbers</li>
        </ul>
        <p>
          If any data is missing or incorrectly extracted, users can manually complete or edit the information. The
          finalized results can be exported as a CSV file for further use.
        </p>
      </v-col>
      <v-col cols="12" md="6">
        <video controls width="100%" preload="none" poster="/images/finish-line-video-analytics.jpg">
          <source src="/videos/finish-line-video-analytics.mp4" type="video/mp4" />
        </video>
      </v-col>
    </v-row>

    <h2>User Guide: Uploading and Processing Finish Line Videos</h2>
    <p>
      This guide walks you through how to upload a finish line video from a sailing race, let the software extract key results, and finalize your race data with manual edits and CSV export.

    </p>
    <h4>Step 1: Upload your Finish Line Video</h4>
    <p>
      After logging in on the website, please navigate to the
      <nuxt-link to="/account/race-videos/upload">upload race video page</nuxt-link>.
    </p>
    <p>
      Click Upload Video and select your race finish video (formats: .mp4, .mov, etc.).
    </p>
    <p>
      The video will upload and begin automatic processing.
    </p>
    <p>
      <v-icon>mdi-information</v-icon>
      Tip: Use a high-resolution video with a clear view of the finish line and visible sail numbers.
    </p>

    <h4>Step 2: Let the Software Do the Work</h4>
    <p>
      Once uploaded, the system will automatically analyze the video to detect:
    </p>
    <ul>
      <li>
        The start of the race
      </li>
      <li>
        The moment each boat crosses the finish line
      </li>
      <li>
        Each boat’s sail number
      </li>
    </ul>
    <p>
      You’ll see a draft list of race results based on the AI's detection.
    </p>

    <h4>Step 3: Review and Edit the Race Results</h4>

    <p>
      After extraction, the data appears in a table with the following columns:
    </p>
    <ul>
      <li>
        Finish Line Picture
      </li>
      <li>
        Sail Number
      </li>
      <li>
        Timestamp in the video in seconds
      </li>
      <li>
        Sailed Time (calculated from the start)
      </li>
      <li>
        Finish Code (e.g., DNF, DNS, DSQ)
      </li>
    </ul>

    <p>
      If something’s missing or incorrect:
    </p>
    <p>
      Click into any field to manually edit.
    </p>
    <p>
      Add a Finish Code if a boat didn’t complete the race.
    </p>
    <p>
      You can also add entries for boats the AI missed.
    </p>
    <p>
      Make sure all the data is correct and save before exporting.
    </p>

    <h4>Step 4: Export to CSV</h4>

    <p>
      Once everything looks good: Click the Export CSV button.
    </p>

    <p>
      The app will download a .csv file with your finalized results.
    </p>

    <v-table>
      <thead>
        <tr>
          <th>Sail Number</th>
          <th>Time in Video [s]</th>
          <th>Time Sailed</th>
          <th>Finish Code</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>SUI 1234</td>
          <td>11</td>
          <td>00:23:41</td>
          <td>-</td>
        </tr>
        <tr>
          <td>GER 5678</td>
          <td>20</td>
          <td>-</td>
          <td>DSC</td>
        </tr>
      </tbody>
    </v-table>

    <h2>Video Demo of the feature</h2>
    <video controls width="100%" preload="none" poster="/images/finish-line-video-processing.jpg">
      <source src="/videos/finish-line-video-processing.mp4" type="video/mp4" />
    </video>

    <h2>FAQ</h2>
    <Faq
        :items="faq"
    />
  </div>
</template>
